import { useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCreateLicense } from "@/api/mutations/licenses";
import { useCustomers } from "@/api/queries/customers";

// Lizenz anlegen. Limits = 4 Zahlenfelder, features kommasepariert → Array, validUntil
// als date-input → ISO. enforcement bewusst weggelassen (Server-Default).
export function CreateLicenseDialog({
  open,
  onOpenChange,
  presetCustomerId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presetCustomerId?: string;
}) {
  const customers = useCustomers();
  const create = useCreateLicense();

  const [customerId, setCustomerId] = useState(presetCustomerId ?? "");
  const [edition, setEdition] = useState("");
  const [features, setFeatures] = useState("");
  const [agents, setAgents] = useState(0);
  const [users, setUsers] = useState(0);
  const [nodes, setNodes] = useState(0);
  const [channels, setChannels] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [trial, setTrial] = useState(false);

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        customerId: customerId.trim(),
        edition: edition.trim(),
        features: features
          .split(",")
          .map((f) => f.trim())
          .filter(Boolean),
        limits: { agents, users, nodes, channels },
        // date-input liefert YYYY-MM-DD → auf Tagesende in UTC normalisieren.
        validUntil: new Date(`${validUntil}T23:59:59Z`).toISOString(),
        trial,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-license-dialog">
        <DialogHeader>
          <DialogTitle>Lizenz anlegen</DialogTitle>
          <DialogDescription>Edition, Features und Limits sind nach dem Anlegen unveraenderlich (Aenderung = neue Lizenz).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lic-customer">Kunde</Label>
            <select
              id="lic-customer"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              required
              disabled={!!presetCustomerId}
              data-testid="license-customer"
              className="flex h-10 w-full rounded-md border border-border bg-base px-3 py-2 text-sm text-text"
            >
              <option value="" disabled>
                — Kunde waehlen —
              </option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lic-edition">Edition</Label>
            <Input id="lic-edition" value={edition} onChange={(e) => setEdition(e.target.value)} required data-testid="license-edition" placeholder="z.B. enterprise" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lic-features">Features (kommasepariert)</Label>
            <Input id="lic-features" value={features} onChange={(e) => setFeatures(e.target.value)} placeholder="a2a, discovery, plugins" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {([
              ["Agents", agents, setAgents],
              ["Users", users, setUsers],
              ["Nodes", nodes, setNodes],
              ["Channels", channels, setChannels],
            ] as const).map(([label, val, set]) => (
              <div key={label} className="space-y-1">
                <Label className="text-[11px]">{label}</Label>
                <Input
                  type="number"
                  min={0}
                  value={val}
                  onChange={(e) => set(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  data-testid={`license-limit-${label.toLowerCase()}`}
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lic-until">Gueltig bis</Label>
            <Input id="lic-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} required data-testid="license-until" />
          </div>
          <label className="flex items-center gap-2 text-sm text-text2">
            <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} data-testid="license-trial" />
            Trial-Lizenz
          </label>
          {create.isError && (
            <div className="text-sm text-danger" data-testid="create-license-error">
              {create.error.message}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={create.isPending} data-testid="create-license-submit">
              {create.isPending ? "…" : "Anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
